/**
 * Check In with Dr. C — API + auth helpers.
 *
 * Talks to /api/checkin/* through REACT_APP_BACKEND_URL. Stores the
 * consumer JWT in localStorage under a namespaced key so KCIS admin
 * tokens can never leak into this app (and vice versa).
 */
const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "checkin.token";
const USER_KEY = "checkin.user";

export function getToken() {
  try { return localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
}
export function setToken(t) {
  try { localStorage.setItem(TOKEN_KEY, t || ""); } catch { /* ignore */ }
}
export function clearAuth() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch { /* ignore */ }
}
export function getCachedUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
export function setCachedUser(u) {
  try { localStorage.setItem(USER_KEY, JSON.stringify(u || null)); } catch { /* ignore */ }
}

async function request(path, { method = "GET", body, auth = false } = {}) {
  const headers = { "Content-Type": "application/json" };
  if (auth) {
    const t = getToken();
    if (!t) throw new Error("Not signed in.");
    headers["Authorization"] = `Bearer ${t}`;
  }
  const r = await fetch(`${API}/api/checkin${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try { data = await r.json(); } catch { /* non-json */ }
  if (!r.ok) {
    const detail = data?.detail;
    const msg = (detail && typeof detail === "object" && detail.message)
      || (detail && typeof detail === "string" ? detail : null)
      || data?.message
      || `Request failed (${r.status})`;
    const err = new Error(msg);
    err.status = r.status;
    err.code = (detail && typeof detail === "object" && detail.code) || null;
    err.detail = detail;
    err.raw = data;
    throw err;
  }
  return data;
}

export const checkinApi = {
  register: (payload) => request("/auth/register", { method: "POST", body: payload }),
  login:    (payload) => request("/auth/login",    { method: "POST", body: payload }),
  me:       ()         => request("/auth/me",       { auth: true }),
  setGreeting: (payload) => request("/onboarding/greeting", { method: "POST", body: payload, auth: true }),
  recordConsent: (payload) => request("/onboarding/consent", { method: "POST", body: payload, auth: true }),
  home:     ()         => request("/home",           { auth: true }),
  catalog:  ()         => request("/catalog"),
  // Phase 3 — conversation, memory, audio
  startConversation: (resume) => request("/conversation/start",
    { method: "POST", body: { resume_conversation_id: resume || null }, auth: true }),
  resumePaidConversation: () => request("/conversation/resume-paid",
    { method: "POST", body: {}, auth: true }),
  sendTurn: (conversation_id, text) => request("/conversation/turn",
    { method: "POST", body: { conversation_id, text }, auth: true }),
  readConversation: (id) => request(`/conversation/${id}`, { auth: true }),
  /**
   * Fetch MP3 audio for an assistant message with the consumer JWT and
   * return a blob URL suitable for an <audio src>. The blob URL is
   * revoked-safe; callers should URL.revokeObjectURL when unmounting.
   */
  fetchAudioBlobUrl: async (message_id) => {
    const t = getToken();
    if (!t) throw new Error("Not signed in.");
    const r = await fetch(`${API}/api/checkin/audio/${message_id}`, {
      headers: { Authorization: `Bearer ${t}` },
    });
    if (!r.ok) {
      let msg = `Audio unavailable (${r.status})`;
      try { const j = await r.json(); msg = j?.detail?.message || msg; } catch { /* ignore */ }
      const err = new Error(msg); err.status = r.status; throw err;
    }
    const blob = await r.blob();
    return URL.createObjectURL(blob);
  },
  saveMemory: (content, source_message_id) => request("/memory",
    { method: "POST", body: { content, source_message_id: source_message_id || null }, auth: true }),
  listMemory: () => request("/memory", { auth: true }),
  deleteMemory: (id) => request(`/memory/${id}`, { method: "DELETE", auth: true }),
  // My Check-Ins (history)
  listConversations: () => request("/conversations", { auth: true }),
  readConversation: (id) => request(`/conversation/${encodeURIComponent(id)}`, { auth: true }),
  renameConversation: (id, title) =>
    request(`/conversation/${encodeURIComponent(id)}`,
             { method: "PATCH", body: { title }, auth: true }),
  deleteConversation: (id) =>
    request(`/conversation/${encodeURIComponent(id)}`,
             { method: "DELETE", auth: true }),

  // Phase 1.5 — Trust · Identity · Relationship
  verifyRequestCode:  (phone) => request("/verify/request-code",
    { method: "POST", body: { phone }, auth: true }),
  verifyConfirmCode:  (phone, code) => request("/verify/confirm-code",
    { method: "POST", body: { phone, code }, auth: true }),
  verifyStatus:       ()      => request("/verify/status", { auth: true }),
  setProfileExtras:   (payload) => request("/profile/extras",
    { method: "POST", body: payload, auth: true }),
  journey:            ()      => request("/journey", { auth: true }),
  conversationSummary: (id)   => request(`/conversation/${encodeURIComponent(id)}/summary`,
    { auth: true }),
  regenerateSummary:  (id)    => request(`/conversation/${encodeURIComponent(id)}/summary/regenerate`,
    { method: "POST", auth: true }),
};
